import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Cookie, Settings, ShieldCheck } from 'lucide-react';
import { useTranslation } from '../../tools/i18n';
import './index.scss';

interface CookieCategory {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  required?: boolean;
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: 'essential',
    titleKey: 'cookies.categories.essential.title',
    descriptionKey: 'cookies.categories.essential.description',
    icon: <ShieldCheck size={22} strokeWidth={2} />,
    required: true,
  },
  {
    id: 'functional',
    titleKey: 'cookies.categories.functional.title',
    descriptionKey: 'cookies.categories.functional.description',
    icon: <Settings size={22} strokeWidth={2} />,
  },
  {
    id: 'analytics',
    titleKey: 'cookies.categories.analytics.title',
    descriptionKey: 'cookies.categories.analytics.description',
    icon: <BarChart3 size={22} strokeWidth={2} />,
  },
];

const CONSENT_COOKIE_NAME = 'cookie_consent';

const getConsent = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = document.cookie
      .split(';')
      .find((c) => c.trim().startsWith(`${CONSENT_COOKIE_NAME}=`));
    if (!raw) return {};
    return JSON.parse(decodeURIComponent(raw.split('=')[1].trim()));
  } catch {
    return {};
  }
};

const saveConsent = (consent: Record<string, boolean>) => {
  const value = encodeURIComponent(JSON.stringify(consent));
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; expires=${expires}; SameSite=Lax`;
};

interface CookieConsentProps {
  open: boolean;
  onClose: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<Record<string, boolean>>({});
  const [isClosing, setIsClosing] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (open) {
      const saved = getConsent();
      const initial: Record<string, boolean> = {};
      COOKIE_CATEGORIES.forEach((cat) => {
        initial[cat.id] = cat.required ? true : saved[cat.id] ?? false;
      });
      setConsent(initial);
      setIsClosing(false);
      setShowDetails(false);
      setIsEntering(true);
      const timer = setTimeout(() => setIsEntering(false), 700);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 320);
  }, [onClose]);

  const handleToggle = (id: string) => {
    const cat = COOKIE_CATEGORIES.find((c) => c.id === id);
    if (cat?.required) return;
    setConsent((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleRejectAll = () => {
    const rejected: Record<string, boolean> = {};
    COOKIE_CATEGORIES.forEach((cat) => {
      rejected[cat.id] = !!cat.required;
    });
    saveConsent(rejected);
    handleClose();
  };

  const handleAcceptSelected = () => {
    saveConsent(consent);
    handleClose();
  };

  const handleAcceptAll = () => {
    const all: Record<string, boolean> = {};
    COOKIE_CATEGORIES.forEach((cat) => {
      all[cat.id] = true;
    });
    saveConsent(all);
    handleClose();
  };

  if (!open) return null;

  return (
    <>
      <div className={`CookieConsent ${isClosing ? 'closing' : ''} ${isEntering ? 'entering' : ''}`}>
        {/* Header decorative bar */}
        <div className="CookieConsent__accent-bar" />

        <div className="CookieConsent__content">
          {/* Header */}
          <div className="CookieConsent__header">
            <div className="CookieConsent__header-icon">
              <Cookie size={28} strokeWidth={2} color="#d97706" />
            </div>
            <div className="CookieConsent__header-text">
              <span className="CookieConsent__greeting">
                {t('cookies.greeting')}
              </span>
              <h2 className="CookieConsent__title">{t('cookies.title')}</h2>
            </div>
            <button
              className="CookieConsent__close"
              onClick={handleClose}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Description */}
          <p className="CookieConsent__description">
            {t('cookies.description')}
          </p>

          {/* Toggle details */}
          <button
            className="CookieConsent__details-toggle"
            onClick={() => setShowDetails((v) => !v)}
          >
            <span>{t('cookies.managePreferences')}</span>
            <svg
              className={`CookieConsent__chevron ${showDetails ? 'open' : ''}`}
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Cookie Categories */}
          <div className={`CookieConsent__categories ${showDetails ? 'open' : ''}`}>
            {COOKIE_CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                className={`CookieConsent__category ${
                  consent[cat.id] ? 'active' : ''
                } ${cat.required ? 'required' : ''}`}
              >
                <div className="CookieConsent__category-icon">{cat.icon}</div>
                <div className="CookieConsent__category-info">
                  <span className="CookieConsent__category-title">
                    {t(cat.titleKey)}
                    {cat.required && (
                      <span className="CookieConsent__badge">
                        {t('cookies.required')}
                      </span>
                    )}
                  </span>
                  <span className="CookieConsent__category-desc">
                    {t(cat.descriptionKey)}
                  </span>
                </div>
                <button
                  className={`CookieConsent__toggle ${
                    consent[cat.id] ? 'on' : 'off'
                  } ${cat.required ? 'locked' : ''}`}
                  onClick={() => handleToggle(cat.id)}
                  aria-label={`Toggle ${cat.id}`}
                  disabled={cat.required}
                >
                  <span className="CookieConsent__toggle-thumb" />
                </button>
              </div>
            ))}
          </div>

          {/* Cookie detail lists — always visible when details expanded */}
          {showDetails && (
            <div className="CookieConsent__cookie-lists">
              <div className="CookieConsent__cookie-group">
                <h4>{t('cookies.categories.essential.title')}</h4>
                <ul>
                  <li>
                    <code>partner</code> — {t('cookies.detail.partner')}
                  </li>
                  <li>
                    <code>environment</code> — {t('cookies.detail.environment')}
                  </li>
                  <li>
                    <code>cookie_consent</code> —{' '}
                    {t('cookies.detail.cookieConsent')}
                  </li>
                </ul>
              </div>
              <div className="CookieConsent__cookie-group">
                <h4>{t('cookies.categories.functional.title')}</h4>
                <ul>
                  <li>
                    <code>cibler_id</code> — {t('cookies.detail.ciblerid')}
                  </li>
                  <li>
                    <code>loginToken</code> — {t('cookies.detail.loginToken')}
                  </li>
                  <li>
                    <code>travelFavorites</code> —{' '}
                    {t('cookies.detail.travelFavorites')}
                  </li>
                  <li>
                    <code>lastTrip</code> — {t('cookies.detail.lastTrip')}
                  </li>
                </ul>
              </div>
              <div className="CookieConsent__cookie-group">
                <h4>{t('cookies.categories.analytics.title')}</h4>
                <ul>
                  <li>
                    <code>dataLayer</code> — {t('cookies.detail.dataLayer')}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="CookieConsent__actions">
          <button
            className="CookieConsent__btn CookieConsent__btn--reject"
            onClick={handleRejectAll}
          >
            {t('cookies.rejectAll')}
          </button>
          <button
            className="CookieConsent__btn CookieConsent__btn--choose"
            onClick={handleAcceptSelected}
          >
            {t('cookies.acceptSelected')}
          </button>
          <button
            className="CookieConsent__btn CookieConsent__btn--accept"
            onClick={handleAcceptAll}
          >
            {t('cookies.acceptAll')}
          </button>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
